import { FailedToGetData, H2, Loading, RadioSelector } from 'src/components/_commons';
import { generalHelpers } from '@monorepo/helpers';
import { LogsCard } from './subcomponents/LogsCard';
import { logServices } from 'src/services';
import { setAlertResponse } from 'src/store';
import { typeLogsDoc } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetCurrentUser } from 'src/custom-hooks';
import { useState, useEffect, useMemo } from 'react';

type typeLogDateRange = {
    maxDate: string | null;
    minDate: string | null;
};

export const AdminsLogs = () => {
    const user = useGetCurrentUser();
    //
    const [congregationForLogs, setCongregationForLogs] = useState(user.congregation);
    const [dateRange, setDateRange] = useState<typeLogDateRange | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [logs, setLogs] = useState<typeLogsDoc[] | null>(null);
    const [maxDateInput, setMaxDateInput] = useState('');
    const [minDateInput, setMinDateInput] = useState('');
    const [radioValue, setRadioValue] = useState('');
    const dispatch = useDispatch();
    //
    const endDate = maxDateInput || (minDateInput ? generalHelpers.GetCurrentLocalDate() : '');
    const isDateRangeInvalid = !!minDateInput && !!endDate && minDateInput > endDate;

    const getLogs = async (filters: typeLogDateRange | null): Promise<boolean> => {
        setIsLoading(true);
        const response = await logServices.GetAll({
            maxDate: filters?.maxDate,
            minDate: filters?.minDate,
            offset: 0
        });
        setIsLoading(false);
        if (!response.success || !response.data?.logs) {
            dispatch(setAlertResponse({ response }));
            return false;
        }
        setLogs(response.data.logs.map(log => ({ ...log, hasMore: true })));
        setRadioValue('');
        return true;
    }

    const logsToShow: typeLogsDoc[] = useMemo(() =>
        logs
         ?.filter(l => l.congregation === congregationForLogs)
         .sort((l1, l2) => l1.title.localeCompare(l2.title))
        ?? []
    , [congregationForLogs, logs]);

    const activeLog: typeLogsDoc | null = useMemo(() =>
        logsToShow.find(l => l.type === radioValue) ?? null
    , [logsToShow, radioValue]);

    const searchLogs = async (): Promise<void> => {
        if (isDateRangeInvalid) {
            return;
        }
        const filters = (minDateInput || maxDateInput) ?
            {
                minDate: minDateInput || null,
                maxDate: endDate || null
            }
            :
            null
        ;
        if (await getLogs(filters)) {
            setDateRange(filters);
        }
    }

    const resetSearch = async (): Promise<void> => {
        setMinDateInput('');
        setMaxDateInput('');
        if (await getLogs(null)) {
            setDateRange(null);
        }
    }

    useEffect(() => {
        void getLogs(null);
    }, []);

    if (isLoading) {
        return <Loading mt={'80px'} />;
    }

    if (!logs) {
        return <FailedToGetData />;
    }

    return (
        <>
        <H2 title={'LOGS DE LA APLICACIÓN'} />
        <form className={'d-flex flex-column align-items-center gap-3 my-4'} onSubmit={event => {
            event.preventDefault();
            void searchLogs();
        }}>
            <div className={'d-flex flex-wrap justify-content-center align-items-end gap-3'}>
                <label className={'text-center'}>
                    Fecha mínima (inclusive)
                    <input
                        type={'date'}
                        className={'form-control'}
                        value={minDateInput}
                        onChange={event => setMinDateInput(event.target.value)}
                    />
                </label>
                <label className={'text-center'}>
                    Fecha máxima (inclusive)
                    <input
                        type={'date'}
                        className={'form-control'}
                        value={maxDateInput}
                        onChange={event => setMaxDateInput(event.target.value)}
                    />
                </label>
            </div>
            {isDateRangeInvalid &&
                <div className={'text-danger'} role={'alert'}>
                    La fecha mínima no puede ser posterior a la fecha máxima.
                </div>
            }
            <div className={'d-flex flex-wrap justify-content-center gap-2'}>
                <button className={'btn btn-general-blue'} type={'submit'} disabled={isDateRangeInvalid}>
                    Buscar
                </button>
                <button className={'btn btn-secondary'} type={'button'} onClick={() => void resetSearch()}>
                    Resetear Búsqueda
                </button>
            </div>
        </form>
        {!!logs.length && logs.some(x => !!x.logs?.length) ?
            <>
            {user.isSuperAdmin &&
                <div className={'d-flex justify-content-center'}>
                    <select className={'form-select my-3 maxw-400'}
                        value={congregationForLogs}
                        onChange={e => {
                            setCongregationForLogs(parseInt(e.target.value) || 0);
                            setRadioValue('');
                        }}
                    >
                        {[...new Set(logs?.map(l => l.congregation))].sort((c1, c2) => c1 - c2).map(c =>
                            <option key={c} value={c}>
                                Congregación {c}
                            </option>
                        )}
                    </select>
                </div>
            }
            {[0, 1, 2, 3].map(n =>
                <RadioSelector key={n}
                    additionalKey={`R${n}`}
                    options={logsToShow.slice(n * 5, 5 * (n + 1)).map(l =>
                        ({ label: l.title, type: l.type })
                    ) ?? []}
                    radioValue={radioValue}
                    removeMargin
                    setRadioValue={setRadioValue}
                />
            )}
            </>
            :
            <h4 className={'text-center mt-5'}>
                No hay datos
            </h4>
        }
        {!!activeLog &&
            <LogsCard
                activeLog={activeLog}
                dateRange={dateRange}
                setLogs={setLogs}
            />
        }
        </>
    );
}
